import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { format, startOfMonth, endOfMonth, eachDayOfInterval, getDay, addMonths, subMonths, isSameDay } from 'date-fns';
import { fr } from 'date-fns/locale';
import { ChevronLeft, ChevronRight, BookOpen, Clock, MapPin, Users, Loader2, CheckCircle2, AlertCircle, ArrowRight, Target } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { DashboardLayout } from '../components/layout/DashboardLayout';
import { getJourFerieLabel } from '../lib/joursFeries';
import { ActiviteFormateurDialog, activiteColor } from '../components/formateur/ActiviteFormateurDialog';

const API_URL = `${process.env.REACT_APP_BACKEND_URL}/api`;

export const MonPlanningPage = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [currentMonth, setCurrentMonth] = useState(new Date());
  const todayStr = format(new Date(), 'yyyy-MM-dd');
  const [dossiers, setDossiers] = useState([]);
  const [activites, setActivites] = useState([]);
  const [activiteOpen, setActiviteOpen] = useState(null);
  const [selectedDay, setSelectedDay] = useState(null);
  const [loading, setLoading] = useState(true);

  const fetchDossiers = useCallback(async () => {
    setLoading(true);
    try {
      const [res, actRes] = await Promise.all([
        axios.get(`${API_URL}/portail/formateur/dossiers`),
        axios.get(`${API_URL}/portail/formateur/activites`).catch(() => ({ data: [] })),
      ]);
      setDossiers(res.data);
      setActivites(actRes.data || []);
    } catch { }
    setLoading(false);
  }, []);

  useEffect(() => { fetchDossiers(); }, [fetchDossiers]);

  // Build planning map: date -> [{dossier}]
  // The API returns assigned_dates (dates where this formateur is assigned)
  const planningMap = {};
  dossiers.forEach(d => {
    (d.assigned_dates || []).forEach(dateStr => {
      if (!planningMap[dateStr]) planningMap[dateStr] = [];
      const saisi = (d.cahier_dates || []).includes(dateStr);
      planningMap[dateStr].push({ dossier: d, saisi, valide: (d.cahier_valide_dates || []).includes(dateStr), aSaisir: !saisi && dateStr <= todayStr });
    });
  });

  const monthStart = startOfMonth(currentMonth);
  const monthEnd = endOfMonth(currentMonth);
  const days = eachDayOfInterval({ start: monthStart, end: monthEnd });
  const startPad = getDay(monthStart) === 0 ? 6 : getDay(monthStart) - 1;

  const selectedDateStr = selectedDay ? format(selectedDay, 'yyyy-MM-dd') : null;
  const selectedEntries = selectedDateStr ? (planningMap[selectedDateStr] || []) : [];
  const activitesByDate = {};
  activites.forEach(a => { (activitesByDate[a.date] = activitesByDate[a.date] || []).push(a); });
  const selectedActivites = selectedDateStr ? (activitesByDate[selectedDateStr] || []) : [];

  const joursSemaine = ['Lun', 'Mar', 'Mer', 'Jeu', 'Ven', 'Sam', 'Dim'];

  if (loading) {
    return (
      <DashboardLayout>
        <div className="flex items-center justify-center h-64">
          <Loader2 className="w-8 h-8 animate-spin text-indigo-600" />
        </div>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout>
    <div className="max-w-5xl mx-auto" data-testid="mon-planning-page">
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <h1 className="text-2xl font-bold text-slate-800">Mon Planning</h1>
        <div className="flex items-center gap-3">
          <button onClick={() => setCurrentMonth(subMonths(currentMonth, 1))} className="p-2 rounded-lg hover:bg-slate-100" data-testid="prev-month">
            <ChevronLeft className="w-5 h-5" />
          </button>
          <span className="text-lg font-semibold text-slate-700 w-48 text-center capitalize">
            {format(currentMonth, 'MMMM yyyy', { locale: fr })}
          </span>
          <button onClick={() => setCurrentMonth(addMonths(currentMonth, 1))} className="p-2 rounded-lg hover:bg-slate-100" data-testid="next-month">
            <ChevronRight className="w-5 h-5" />
          </button>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Calendar */}
        <div className="lg:col-span-2 bg-white rounded-xl border shadow-sm p-4">
          <div className="grid grid-cols-7 gap-1 mb-2">
            {joursSemaine.map(j => (
              <div key={j} className="text-center text-xs font-semibold text-slate-400 py-1">{j}</div>
            ))}
          </div>
          <div className="grid grid-cols-7 gap-1">
            {Array.from({ length: startPad }).map((_, i) => (
              <div key={`pad-${i}`} className="h-20" />
            ))}
            {days.map(day => {
              const dateStr = format(day, 'yyyy-MM-dd');
              const entries = planningMap[dateStr] || [];
              const isToday = isSameDay(day, new Date());
              const isSelected = selectedDay && isSameDay(day, selectedDay);
              const isWeekend = getDay(day) === 0 || getDay(day) === 6;
              const ferieLabel = getJourFerieLabel(dateStr);

              return (
                <div
                  key={dateStr}
                  onClick={() => setSelectedDay(day)}
                  title={ferieLabel || undefined}
                  className={`h-20 rounded-lg p-1.5 cursor-pointer transition-all border ${
                    isSelected ? 'border-indigo-500 bg-indigo-50 ring-1 ring-indigo-300' :
                    isToday ? 'border-blue-300 bg-blue-50' :
                    ferieLabel ? 'border-rose-200 bg-rose-50' :
                    isWeekend ? 'border-slate-100 bg-slate-50' :
                    entries.length > 0 ? 'border-emerald-200 bg-emerald-50 hover:border-emerald-400' :
                    'border-slate-100 hover:border-slate-300'
                  }`}
                  data-testid={`day-${dateStr}`}
                >
                  <div className={`text-xs font-semibold mb-0.5 ${isToday ? 'text-blue-600' : ferieLabel ? 'text-rose-600' : isWeekend ? 'text-slate-300' : 'text-slate-600'}`}>
                    {format(day, 'd')}
                  </div>
                  {ferieLabel && entries.length === 0 && (
                    <div className="text-[9px] text-rose-600 truncate" data-testid={`ferie-${dateStr}`}>{ferieLabel}</div>
                  )}
                  {entries.slice(0, 2).map((e) => (
                    <div
                      key={e.dossier.id}
                      className={`text-[9px] rounded px-1 py-0.5 truncate mb-0.5 font-medium flex items-center gap-0.5 ${e.saisi ? 'text-emerald-700 bg-emerald-100' : e.aSaisir ? 'text-amber-700 bg-amber-100' : 'text-indigo-700 bg-indigo-100'}`}
                      title={e.saisi ? 'Cahier de texte saisi' : e.aSaisir ? 'Cahier de texte à saisir' : 'À venir'}
                      data-testid={`chip-${dateStr}-${e.dossier.id}-${e.saisi ? 'saisi' : e.aSaisir ? 'a-saisir' : 'a-venir'}`}
                    >
                      {e.saisi ? <CheckCircle2 className="w-2.5 h-2.5 shrink-0" /> : e.aSaisir ? <AlertCircle className="w-2.5 h-2.5 shrink-0" /> : null}
                      <span className="truncate">{e.dossier.code || e.dossier.intitule?.substring(0, 15)}</span>
                    </div>
                  ))}
                  {entries.length > 2 && (
                    <div className="text-[9px] text-slate-400 font-medium">+{entries.length - 2} autre{entries.length - 2 > 1 ? 's' : ''}</div>
                  )}
                  {entries.length < 2 && (activitesByDate[dateStr] || []).slice(0, 2 - entries.length).map(a => (
                    <div key={a.id} className={`text-[9px] rounded px-1 py-0.5 truncate mb-0.5 font-medium flex items-center gap-0.5 ${activiteColor(a.type_affectation).chip}`} title={`${a.label}${a.objectif ? ' — ' + a.objectif : ''}`} data-testid={`chip-activite-${dateStr}-${a.id}`}>
                      {a.realise ? <CheckCircle2 className="w-2.5 h-2.5 shrink-0" /> : <Target className="w-2.5 h-2.5 shrink-0" />}
                      <span className="truncate">{activiteColor(a.type_affectation).short} · {a.label}</span>
                    </div>
                  ))}
                </div>
              );
            })}
          </div>

          {/* Legend */}
          <div className="flex flex-wrap gap-4 mt-4 pt-3 border-t" data-testid="planning-legende">
            <div className="flex items-center gap-1.5"><div className="w-3 h-3 rounded bg-emerald-100 border border-emerald-300" /><span className="text-[10px] text-slate-500">Cahier de texte saisi</span></div>
            <div className="flex items-center gap-1.5"><div className="w-3 h-3 rounded bg-amber-100 border border-amber-300" /><span className="text-[10px] text-slate-500">Cahier à saisir</span></div>
            <div className="flex items-center gap-1.5"><div className="w-3 h-3 rounded bg-indigo-100 border border-indigo-300" /><span className="text-[10px] text-slate-500">À venir</span></div>
            <div className="flex items-center gap-1.5"><div className="w-3 h-3 rounded bg-blue-100 border border-blue-300" /><span className="text-[10px] text-slate-500">Aujourd'hui</span></div>
          </div>
        </div>

        {/* Detail panel */}
        <div className="bg-white rounded-xl border shadow-sm p-4">
          <h3 className="text-sm font-bold text-slate-700 mb-3">
            {selectedDay ? format(selectedDay, 'EEEE d MMMM yyyy', { locale: fr }) : 'Sélectionnez un jour'}
          </h3>

          {!selectedDay && (
            <p className="text-xs text-slate-400 mt-8 text-center">Cliquez sur un jour pour voir le détail</p>
          )}

          {selectedDay && selectedEntries.length === 0 && selectedActivites.length === 0 && (
            <div className="text-center py-8">
              <Clock className="w-8 h-8 text-slate-200 mx-auto mb-2" />
              <p className="text-xs text-slate-400">Aucune affectation ce jour</p>
            </div>
          )}

          {selectedActivites.map(a => (
            <button type="button" key={a.id} onClick={() => setActiviteOpen(a)}
              className="w-full text-left mb-3 p-3 rounded-lg border border-blue-200 bg-blue-50 hover:bg-blue-100 transition-colors group" data-testid={`tile-activite-${a.id}`}>
              <div className="flex items-start gap-2">
                <Target className="w-4 h-4 text-blue-600 mt-0.5 shrink-0" />
                <div className="min-w-0 flex-1">
                  <p className="text-xs font-bold text-blue-900">{a.label}</p>
                  {a.objectif && <p className="text-[10px] text-blue-800 mt-0.5 line-clamp-3"><span className="font-semibold">Objectif :</span> {a.objectif}</p>}
                </div>
                <ArrowRight className="w-3.5 h-3.5 text-blue-300 group-hover:text-blue-600 shrink-0" />
              </div>
              <div className="mt-2 flex items-center gap-1.5">
                {a.realise ? (
                  <span className="inline-flex items-center gap-1 text-[9px] px-2 py-0.5 rounded-full font-medium bg-emerald-100 text-emerald-700"><CheckCircle2 className="w-2.5 h-2.5" /> Réalisé renseigné</span>
                ) : (
                  <span className="inline-flex items-center gap-1 text-[9px] px-2 py-0.5 rounded-full font-medium bg-amber-100 text-amber-700"><AlertCircle className="w-2.5 h-2.5" /> Réalisé à saisir</span>
                )}
                <span className="text-[9px] text-blue-700 font-medium ml-auto group-hover:underline">Ouvrir →</span>
              </div>
            </button>
          ))}

          {selectedEntries.map((entry) => (
            <button
              type="button"
              key={entry.dossier.id}
              onClick={() => navigate(`/portail-formateur?dossier=${entry.dossier.id}&date=${selectedDateStr}`)}
              className="w-full text-left mb-3 p-3 rounded-lg border bg-slate-50 hover:bg-indigo-50 hover:border-indigo-300 transition-colors group"
              title="Ouvrir la saisie de la journée"
              data-testid={`tile-${entry.dossier.id}`}
            >
              <div className="flex items-start gap-2 mb-2">
                <BookOpen className="w-4 h-4 text-indigo-500 mt-0.5 shrink-0" />
                <div className="min-w-0 flex-1">
                  <p className="text-xs font-bold text-slate-800">{entry.dossier.code}</p>
                  <p className="text-[10px] text-slate-500">{entry.dossier.intitule}</p>
                </div>
                <ArrowRight className="w-3.5 h-3.5 text-slate-300 group-hover:text-indigo-500 shrink-0" />
              </div>
              {entry.dossier.lieu_principal && (
                <div className="flex items-center gap-1.5 mb-1">
                  <MapPin className="w-3 h-3 text-slate-400" />
                  <span className="text-[10px] text-slate-600">{entry.dossier.lieu_principal}</span>
                </div>
              )}
              <div className="flex items-center gap-1.5">
                <Users className="w-3 h-3 text-slate-400" />
                <span className="text-[10px] text-slate-600">{entry.dossier.candidats_count || 0} candidat(s)</span>
              </div>
              <div className="mt-2 flex items-center gap-1.5 flex-wrap">
                {entry.saisi ? (
                  <span className="inline-flex items-center gap-1 text-[9px] px-2 py-0.5 rounded-full font-medium bg-emerald-100 text-emerald-700"><CheckCircle2 className="w-2.5 h-2.5" /> Cahier de texte saisi{entry.valide ? ' · présences validées' : ''}</span>
                ) : entry.aSaisir ? (
                  <span className="inline-flex items-center gap-1 text-[9px] px-2 py-0.5 rounded-full font-medium bg-amber-100 text-amber-700"><AlertCircle className="w-2.5 h-2.5" /> Cahier de texte à saisir</span>
                ) : (
                  <span className="text-[9px] px-2 py-0.5 rounded-full font-medium bg-indigo-100 text-indigo-700">À venir</span>
                )}
                <span className="text-[9px] text-indigo-600 font-medium ml-auto group-hover:underline">Ouvrir la journée →</span>
              </div>
            </button>
          ))}
        </div>
      </div>
      <ActiviteFormateurDialog activite={activiteOpen} open={!!activiteOpen} onOpenChange={(o) => !o && setActiviteOpen(null)}
        onSaved={(a) => setActivites(prev => prev.map(x => x.id === a.id ? { ...x, ...a } : x))} />
    </div>
    </DashboardLayout>
  );
};
